import { useEffect, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { getUsersApi } from "@praesto/core";
import type { AvailabilityRule, AvailabilityRuleType } from "@praesto/shared-types";
import {
  Input,
  Select,
  Button,
  FormField,
  Card,
  EmptyState,
  SkeletonFormSection,
  AvailabilityChip,
} from "@praesto/ui-kit";

const dayNames = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

interface NewRuleForm {
  type: AvailabilityRuleType;
  dayOfWeek?: number | undefined;
  slot?: "M" | "P" | "N" | undefined;
  validFrom?: string | undefined;
  validTo?: string | undefined;
  reason?: string | undefined;
}

export function ProfileAvailabilityTab() {
  const { uid } = useParams({ strict: false }) as { uid: string };
  const [rules, setRules] = useState<AvailabilityRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<NewRuleForm>({ type: "unavailable" });

  async function reload() {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const r = await fetch(`${api.apiBase}/api/users/${uid}/availability-rules`, {
      headers: { Authorization: `Bearer ${tok}` },
    });
    const data = (await r.json()) as { rules: AvailabilityRule[] };
    setRules(data.rules);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await reload();
    })()
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  async function addRule() {
    if (form.dayOfWeek === undefined && !form.slot) {
      setError("Specifica almeno un giorno o uno slot.");
      return;
    }
    setError(null);
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    const body: Record<string, unknown> = { userId: uid, type: form.type };
    if (form.dayOfWeek !== undefined) body["dayOfWeek"] = form.dayOfWeek;
    if (form.slot) body["slot"] = form.slot;
    if (form.validFrom) body["validFrom"] = form.validFrom;
    if (form.validTo) body["validTo"] = form.validTo;
    if (form.reason) body["reason"] = form.reason;
    const res = await fetch(`${api.apiBase}/api/users/availability-rules`, {
      method: "POST",
      headers: { Authorization: `Bearer ${tok}`, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = (await res.json()) as { error?: string };
      setError(err.error ?? "Errore");
      return;
    }
    setShowForm(false);
    setForm({ type: "unavailable" });
    await reload();
  }

  async function removeRule(id: string) {
    const api = getUsersApi();
    const tok = await api.getAuthToken();
    await fetch(`${api.apiBase}/api/users/availability-rules/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${tok}` },
    });
    await reload();
  }

  if (loading) return <SkeletonFormSection fields={4} />;

  return (
    <div className="space-y-6 max-w-3xl">
      {error && <p className="text-sm text-danger">{error}</p>}

      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="text-eyebrow">Regole ricorrenti</div>
          {!showForm && (
            <Button size="md" variant="secondary" onClick={() => setShowForm(true)}>
              Nuova regola
            </Button>
          )}
        </div>

        {showForm && (
          <Card tone="subtle" padding="md" className="mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <FormField label="Tipo">
                <Select
                  value={form.type}
                  onChange={(e) =>
                    setForm({ ...form, type: e.target.value as AvailabilityRuleType })
                  }
                >
                  <option value="unavailable">Non disponibile (vincolante)</option>
                  <option value="preferred_off">Preferirei non lavorare</option>
                  <option value="preferred_on">Preferirei lavorare</option>
                </Select>
              </FormField>
              <FormField label="Giorno">
                <Select
                  value={form.dayOfWeek ?? ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      dayOfWeek: e.target.value === "" ? undefined : Number(e.target.value),
                    })
                  }
                >
                  <option value="">Ogni giorno</option>
                  {dayNames.map((d, i) => (
                    <option key={i} value={i}>
                      {d}
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Slot">
                <Select
                  value={form.slot ?? ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      slot: (e.target.value || undefined) as "M" | "P" | "N" | undefined,
                    })
                  }
                >
                  <option value="">Ogni slot</option>
                  <option value="M">Mattino</option>
                  <option value="P">Pomeriggio</option>
                  <option value="N">Notte</option>
                </Select>
              </FormField>
              <FormField label="Da">
                <Input
                  type="date"
                  value={form.validFrom ?? ""}
                  onChange={(e) => setForm({ ...form, validFrom: e.target.value || undefined })}
                />
              </FormField>
              <FormField label="A">
                <Input
                  type="date"
                  value={form.validTo ?? ""}
                  onChange={(e) => setForm({ ...form, validTo: e.target.value || undefined })}
                />
              </FormField>
              <div className="sm:col-span-3">
                <FormField label="Motivo (opzionale)">
                  <Input
                    maxLength={200}
                    value={form.reason ?? ""}
                    onChange={(e) => setForm({ ...form, reason: e.target.value || undefined })}
                  />
                </FormField>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-3 sm:flex-row">
              <Button size="md" variant="primary" className="w-full sm:w-auto" onClick={addRule}>
                Aggiungi
              </Button>
              <Button
                size="md"
                variant="ghost"
                className="w-full sm:w-auto"
                onClick={() => {
                  setShowForm(false);
                  setError(null);
                }}
              >
                Annulla
              </Button>
            </div>
          </Card>
        )}

        {rules.length === 0 ? (
          <EmptyState
            title="Nessuna regola ricorrente"
            description="Le regole dicono al planner quando questa persona non c'è o preferisce non lavorare: aggiungine una con «Nuova regola»."
            size="compact"
          />
        ) : (
          <div className="flex flex-wrap gap-2">
            {rules.map((r) => (
              <AvailabilityChip key={r.id} rule={r} onRemove={() => void removeRule(r.id)} />
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="text-eyebrow mb-2">Override mensile</div>
        <p className="text-sm text-fg-muted">
          La disponibilità mensile per la pianificazione turni si imposta nel modulo Turni →
          Disponibilità.
        </p>
      </section>
    </div>
  );
}
